<script>
  import { onMount, getContext } from 'svelte';
  import { goto } from '@sapper/app';

  import { determineProductFromPrice } from '../utils/pricing';

  import { webPSupport } from '../stores/WebP.js';
  import { designState, newProductColour, newProductSublimationTemplate } from '../stores/Designer.js';
  import { konvaStageId } from '../stores/Preview.js';
  import { productDataStore } from '../stores/ProductData.js';

  import Card from './Card.svelte';
  import determineProductColourImageUrl from '../utils/determineProductColourImageUrl.js';
  import ProductColoursGrid from './ProductColoursGrid.svelte';
  import ImagePlaceHolder from './ImagePlaceHolder.svelte';

  export let item;
  export let preferredGender;
  export let overrideDesignerProduct = false;
  export let invert = false;
  export let itemListName = 'filter-list';

  const defaultBulkDiscounts = getContext('defaultBulkDiscounts');
  const productSpecificBulkDiscounts = item.bulkDiscounts;

  const displayPrice = determineProductFromPrice(item.price, productSpecificBulkDiscounts, defaultBulkDiscounts);

  // use cover image on initial load. Once user selects a colour we will use the selected colour image
  let useCoverImageForPreview = item.useCoverImageForPreview;

  let colourRows;

  let imageEl;
  let initialHeight = 0;

  let imageSource;
  let isLoading = false;

  let selectedGender = preferredGender;

  function handleImageLoad() {
    // Only set the initialHeight once (if desired)
    if (!initialHeight) {
      initialHeight = imageEl.offsetHeight;
    }
  }

  $: imageColourObject = item.imageColourObject;
  $: (async () => {
    isLoading = true;
    if (!item || !imageColourObject || !selectedGender) return null;
    imageSource = await setImageUrlandColourObject(item, imageColourObject, selectedGender, useCoverImageForPreview);
    isLoading = false;
  })();

  let ext;
  if ($webPSupport) {
    ext = 'webp';
  } else {
    ext = 'jpg';
  }

  const defaultSublimationTemplate = item.sublimationTemplates
    ? item.sublimationTemplates.find((template) => template.isDefault) || item.sublimationTemplates[0]
    : null;

  function onImgError(event) {
    event.target.src = 'img/design/not-available-for-this-gender-selection.jpg';
  }

  function determineNumberOfColourRows() {
    if (typeof window !== 'undefined') {
      let box = document.querySelector('.colours');
      let width = box.offsetWidth;
      let rows = Math.floor(width / 25) - 1;
      return rows;
    }
  }

  function productClick(product) {
    // Send select_item data to Google Analytics
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ ecommerce: null });
    window.dataLayer.push({
      event: 'select_item',
      ecommerce: {
        items: [
          {
            item_name: product.name,
            price: (product.price / 100).toFixed(2),
            quantity: 1,
            currency: 'AUD',
            item_category: product.category,
            item_category2: product.collection,
            item_category3: product.type,
            item_category4: false,
            item_variant: imageColourObject.displayName,
            item_list_name: itemListName,
          },
        ],
      },
    });

    let destination = product.isSublimationProduct
      ? `/design/${item.slug}/choose-template?gender=${selectedGender}&colour=${imageColourObject.pathName}`
      : `/design/${item.slug}/choose-colours?gender=${selectedGender}&colour=${imageColourObject.pathName}`;

    // TODO - Need to handle situation where customer has multiple items selected - e.g. top, bottom, hat and then changes the product?
    // Crude solution is to delete all other items from the productDataStore and from designState
    let arrayOfSelectedProducts = designState.arrayOfSelectedProducts($konvaStageId);
    for (let i = 0; i < arrayOfSelectedProducts.length; i++) {
      let existingProductType = arrayOfSelectedProducts[i];
      if (existingProductType !== product.type) {
        designState.deleteProduct($konvaStageId, existingProductType);
        productDataStore.deleteProduct(existingProductType);
      }
    }

    if (overrideDesignerProduct && $designState.length > 0) {
      let designId = $designState[0].id;
      let productObject = {
        id: product.id,
        type: product.type,
        slug: product.slug,
        rootSlug: product.rootSlug,
        name: product.name,
        price: product.price,
        isModelBasedImages: product.isModelBasedImages,
        useCoverImageForPreview: product.useCoverImageForPreview,
      };
      // If override set and previous design exists then we need to update the designState before navigating
      // Note the imageColourObject data has to match that from /design/layout and the designState

      // Need to update the productDataStore with selected colour
      // check if product data exists already in the store for this productType
      if ($productDataStore[product.type]) {
        $productDataStore[product.type].selectedColour = imageColourObject;
        designState.addProduct(designId, productObject, imageColourObject, defaultSublimationTemplate);
      } else {
        // user switched to a product that was new - e.g. from top to bottom and bottom didn't exist before
        $productDataStore[product.type] = {
          selectedColour: imageColourObject,
        };
      }
      $productDataStore[product.type].selectedColour = imageColourObject;
      designState.addProduct(designId, productObject, imageColourObject, defaultSublimationTemplate);
      goto(destination);
    } else {
      // Override not set - so we will load product or launch the modal if previous design exists
      // Set a new product colour object and sublimation template
      $newProductColour = imageColourObject;
      $newProductSublimationTemplate = defaultSublimationTemplate;

      goto(destination);
    }
  }

  function changeColour(colourObject) {
    useCoverImageForPreview = false;
    imageColourObject = colourObject;
  }

  async function setImageUrlandColourObject(item, colourObject, selectedGender, useCoverImageForPreview = false) {
    if (!item || !colourObject || !selectedGender) return null;
    return await determineProductColourImageUrl({
      productSlug: item?.rootSlug ? item.rootSlug : item?.slug,
      colourPathName: colourObject.pathName,
      colourObject: colourObject,
      imageSize: 256,
      imageType: ext,
      gender: selectedGender,
      availableGenders: [
        colourObject.mens ? 'mens' : '',
        colourObject.womens ? 'womens' : '',
        colourObject.youth ? 'youth' : '',
      ],
      side: 'front',
      showModel: true,
      isModelBasedImages: item.isModelBasedImages,
      useCoverImageForPreview: useCoverImageForPreview ? item.useCoverImageForPreview : false,
      sublimationTemplateId: defaultSublimationTemplate ? defaultSublimationTemplate?.SublimationTemplate.id : null,
    });
  }

  function changeGender(gender) {
    selectedGender = gender;
  }

  function windowResize() {
    colourRows = determineNumberOfColourRows();
    handleImageLoad();
  }

  onMount(() => {
    colourRows = determineNumberOfColourRows();
  });
</script>

<svelte:window on:resize={windowResize} />
<!-- svelte-ignore a11y-click-events-have-key-events -->
<div class="item" on:click={() => productClick(item)}>
  <Card {invert} border={false}>
    {#if item.genders}
      <div class="genders">
        {#each item.genders as gender}
          <!-- svelte-ignore a11y-click-events-have-key-events -->
          <small
            class:selected-gender={gender === selectedGender}
            on:click|stopPropagation={() => changeGender(gender)}
            on:mouseenter|stopPropagation={() => changeGender(gender)}>{gender[0]}</small
          >
        {/each}
        <div class="item-price"><span class="price-from">from</span>${displayPrice}</div>
      </div>
    {/if}
    {#if isLoading}
      <ImagePlaceHolder height={'256px'} />
    {:else}
      <img
        bind:this={imageEl}
        src={imageSource}
        on:error={onImgError}
        on:load={handleImageLoad}
        alt=""
        style="min-height: {initialHeight}px;"
      />
    {/if}
    <div class="info">
      <div class="categories">{item.category}</div>
      <div class="supplier">{item.supplier}</div>
      <div class="name">
        <strong title={item.name}>{item.name}</strong>
      </div>
      <ProductColoursGrid {item} changeColourFn={changeColour} {colourRows} />
      {#if item.colours.length > colourRows}
        <div class="more-colours">
          +{item.colours.length - colourRows} more colours
        </div>
      {:else}
        <div class="more-colours none">+0 more colours</div>
      {/if}
    </div>
  </Card>
  <div class="design">
    <!-- svelte-ignore a11y-click-events-have-key-events -->
    <span on:click={() => productClick(item)}>Design now</span>
    <a href="/product/{item.slug}"><span>Info</span></a>
  </div>
</div>

<style>
  .item {
    align-self: stretch;
    background-color: white;
    max-width: 90vw;
  }

  .info {
    margin: 1rem 0 0 0;
  }

  .categories {
    font-size: 400;
    letter-spacing: 2px;
    color: #a4a4a4;
    text-transform: uppercase;
    font-size: 1rem;
    margin-bottom: 0.5rem;
    text-align: left;
  }

  .item img {
    width: 100%;
    object-fit: cover;
  }

  .name {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .info strong {
    text-transform: uppercase;
    font-size: 1.5rem;
    font-family: 'Oswald', sans-serif;
  }

  .supplier {
    font-family: 'Oswald';
    text-transform: uppercase;
    font-size: 1.4rem;
    color: #535353;
  }

  .more-colours {
    margin-top: 0.2rem;
    font-size: 1.3rem;
    font-weight: 600;
    color: #6b6a6a;
  }

  .more-colours.none {
    color: white;
  }

  .design {
    background-color: #000;
    border-bottom: 2px solid #fe5900;
    padding: 0.5rem;
    display: grid;
    grid-template-columns: 70% 30%;
  }

  .design > *:first-child {
    border-right: 1px solid #777;
  }

  .design > *:last-child {
    color: #ddd;
    text-align: center;
  }

  .design span {
    cursor: pointer;
    text-transform: uppercase;
    display: inline-block;
    color: #fff;
    font-family: 'Oswald', sans-serif;
  }

  .genders {
    text-align: left;
    margin-bottom: 1rem;
    display: flex;
    align-items: center;
  }

  .price-from {
    font-size: 1.3rem;
    margin-right: 0.5rem;
  }

  .item-price {
    width: 100%;
    display: flex;
    justify-content: flex-end;
    align-items: center;
    font-size: 2rem;
    font-family: 'Oswald', sans-serif;
    color: #fe5900;
  }

  small {
    color: #8c8c8c;
    border: 1px solid #ddd;
    height: 2.5rem;
    width: 2.5rem;
    margin: 0.2rem;
    display: flex;
    align-items: center;
    justify-content: center;
    font-family: 'Oswald', sans-serif;
    text-transform: uppercase;
  }

  small.selected-gender {
    border: 1px solid #fe5900;
  }

  a {
    text-decoration: none;
    color: #fff;
  }

  @media (max-width: 600px) {
    small {
      width: 4rem;
      height: 4rem;
      font-size: 2rem;
    }

    .item-price {
      font-size: 2.4rem;
    }
  }

  @media (max-width: 400px) {
    small {
      width: 3rem;
      height: 3rem;
      font-size: 1.8rem;
    }

    .item-price {
      font-size: 2rem;
    }
  }
</style>
